import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import {
  headerCellStyle,
  cellStyle,
} from "../../components/tableHelpers/TableStyleUtils";
import "./TopsTables.css";

//region Helpers

/**
 * Reads UnitPrice from a quote in a safe/normalized way.
 *
 * Important:
 * - the default dashboard model now calculates top references using UnitPrice;
 * - therefore this table must also compare quotes using UnitPrice;
 * - BidValue is no longer the comparison source here.
 *
 * @param {object} quote - TenderQuote row.
 * @returns {number|null} Normalized UnitPrice or null.
 */
const getQuoteUnitPrice = (quote) => {
  const value = Number(quote?.UnitPrice ?? quote?.unitPrice);
  return Number.isFinite(value) ? value : null;
};

//endregion

export default function TopsByCarrierTable({ model }) {
  const { t } = useTranslation();

  //region Compute table data

  /**
   * Builds the carrier statistics table.
   *
   * Semantics:
   * - top1 => quote UnitPrice exactly equals the lane best UnitPrice
   * - topN => quote UnitPrice is <= the N-th best UnitPrice reference
   *
   * Important:
   * - the references in model.topByLane are already UnitPrice-based;
   * - this table must stay aligned with that same semantic.
   */
  const tableData = useMemo(() => {
    const stats = new Map();

    (model.normCarriers || []).forEach((carrier) => {
      stats.set(carrier.id, {
        name: carrier.name,
        totalQuotes: 0,
        top1Count: 0,
        topNCount: 0,
      });
    });

    model.quotesByLane.forEach((laneQuotes, laneId) => {
      const ref = model.topByLane.get(laneId) || { top1: null, topN: null };

      laneQuotes.forEach((quote) => {
        const carrierId = quote.CarrierId ?? quote.Carrier?.Id;
        if (carrierId == null) return;

        if (!stats.has(carrierId)) {
          stats.set(carrierId, {
            name: quote.CarrierName ?? String(carrierId),
            totalQuotes: 0,
            top1Count: 0,
            topNCount: 0,
          });
        }

        const s = stats.get(carrierId);
        const unitPrice = getQuoteUnitPrice(quote);

        s.totalQuotes += 1;

        if (unitPrice == null) return;

        if (ref.top1 != null && unitPrice === Number(ref.top1)) {
          s.top1Count += 1;
          s.topNCount += 1;
        } else if (ref.topN != null && unitPrice <= Number(ref.topN)) {
          s.topNCount += 1;
        }
      });
    });

    const pct = (num, den) =>
      den > 0 ? `${Math.round((num / den) * 100)}%` : "-";

    return (model.normCarriers || []).map((carrier) => {
      const s = stats.get(carrier.id) ?? {
        name: carrier.name,
        totalQuotes: 0,
        top1Count: 0,
        topNCount: 0,
      };

      return {
        name: s.name,
        top1: pct(s.top1Count, s.totalQuotes),
        top2: pct(s.topNCount, s.totalQuotes),
      };
    });
  }, [model]);

  //endregion

  //region Render

  return (
    <div className="tops-table-wrapper" style={{ marginTop: "40px" }}>
      <span
        style={{
          fontWeight: "bold",
          color: "var(--color-red-dark)",
          fontSize: "16px",
          display: "flex",
          marginBottom: "10px",
          borderBottom: "2px solid var(--color-red-dark)",
        }}
      >
        {t("TopQuotesByCarrier")}
      </span>

      <div className="tops-table-container">
        <table className="tops-table tops-table-carrier">
          <thead>
            <tr>
              <th style={headerCellStyle()} className="tops-col-carrier">
                {t("Carrier")}
              </th>
              <th style={headerCellStyle()} className="tops-col-small">
                {t("Top1")}
              </th>
              <th style={headerCellStyle()} className="tops-col-small">
                {t("Top2")}
              </th>
            </tr>
          </thead>
          <tbody>
            {tableData.map((row, idx) => (
              <tr key={idx}>
                <td
                  style={cellStyle({ background: "var(--color-white)" })}
                  className="tops-col-carrier tops-cell"
                >
                  {row.name}
                </td>
                <td
                  style={cellStyle({ background: "var(--color-white)" })}
                  className="tops-col-small tops-cell"
                >
                  {row.top1}
                </td>
                <td
                  style={cellStyle({ background: "var(--color-white)" })}
                  className="tops-col-small tops-cell"
                >
                  {row.top2}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  //endregion
}

TopsByCarrierTable.propTypes = {
  model: PropTypes.shape({
    normCarriers: PropTypes.array.isRequired,
    quotesByLane: PropTypes.instanceOf(Map).isRequired,
    topByLane: PropTypes.instanceOf(Map).isRequired,
  }).isRequired,
};